import type { ReactNode } from 'react';
import { Badge, type BadgeTone } from '@paige/ui';
import type { EnterpriseQuote } from './pb-types';
import { ScrollTable, Th } from './pb-parts';
import { day, perPage, quoteTerms } from './pb-words';

const TONE: Record<EnterpriseQuote['state'], BadgeTone> = {
  offered: 'accent',
  accepted: 'success',
  superseded: 'neutral',
  withdrawn: 'neutral',
};

type ListProps = {
  quotes: EnterpriseQuote[] | undefined;
  canWrite: boolean;
  onWithdraw: (q: EnterpriseQuote) => void;
};

/** The company's Enterprise quotes, newest first: never edited, so new terms are a new quote; an offered one may be withdrawn. */
export function QuoteList(p: ListProps): ReactNode {
  if (!p.quotes || p.quotes.length === 0) return null;
  const head = (
    <>
      <Th>Quote</Th>
      <Th>Terms</Th>
      <Th>Valid until</Th>
      <Th>State</Th>
      {p.canWrite ? (
        <Th>
          <span className="pui-visually-hidden">Withdraw</span>
        </Th>
      ) : null}
    </>
  );
  return (
    <>
      <h4 className="pb-section-title">Enterprise quotes</h4>
      <ScrollTable head={head} label="Enterprise quotes">
        {p.quotes.map((q) => (
          <QuoteRow key={q.id} q={q} {...p} />
        ))}
      </ScrollTable>
    </>
  );
}

function QuoteRow(p: ListProps & { q: EnterpriseQuote }): ReactNode {
  const q = p.q;
  return (
    <tr>
      <td>
        {q.quoteRef ?? 'No reference'}
        <span className="pb-table__sub">{q.quotedBy}</span>
      </td>
      <td>
        {quoteTerms(q)}
        {q.packRatePerPage !== null ? (
          <span className="pb-table__sub">packs {perPage(q.packRatePerPage)} a page</span>
        ) : null}
      </td>
      <td>{day(q.validUntil)}</td>
      <td>
        <Badge tone={TONE[q.state]}>{q.state}</Badge>
      </td>
      {p.canWrite ? (
        <td>
          {q.state === 'offered' ? (
            <button
              type="button"
              className="pb-link"
              aria-label={`Withdraw ${q.quoteRef ?? 'quote'}`}
              onClick={() => p.onWithdraw(q)}
            >
              Withdraw
            </button>
          ) : null}
        </td>
      ) : null}
    </tr>
  );
}
